import React, { useEffect, useRef } from 'react';
import { Search, Loader2 } from 'lucide-react';
import {
  ENTITY_ICONS,
  ENTITY_COLORS,
  ENTITY_LABELS,
  type SearchEntityType,
  type GlobalSearchResult,
} from '../../services/globalSearchService';

interface GlobalSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  query: string;
  setQuery: (q: string) => void;
  results: Map<SearchEntityType, GlobalSearchResult[]>;
  isLoading: boolean;
  totalCount: number;
  activeIndex: number;
  setActiveIndex: (i: number) => void;
  flatResults: GlobalSearchResult[];
  handleKeyDown: (e: React.KeyboardEvent) => void;
  handleResultSelect: (result: GlobalSearchResult) => void;
}

export const GlobalSearchModal: React.FC<GlobalSearchModalProps> = ({
  isOpen,
  onClose,
  query,
  setQuery,
  results,
  isLoading,
  totalCount,
  activeIndex,
  setActiveIndex,
  handleKeyDown,
  handleResultSelect,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Autofocus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Scroll active item into view
  useEffect(() => {
    if (!resultsRef.current) return;
    const activeEl = resultsRef.current.querySelector(`[data-index="${activeIndex}"]`);
    if (activeEl) {
      activeEl.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  if (!isOpen) return null;

  // Build ordered entity types that have results
  const entityOrder: SearchEntityType[] = [];
  for (const [type] of results) {
    entityOrder.push(type);
  }

  // Get flat index for a result given its entity type and position within that group
  const getFlatIndex = (entityType: SearchEntityType, localIdx: number): number => {
    let idx = 0;
    for (const [type, items] of results) {
      if (type === entityType) return idx + localIdx;
      idx += items.length;
    }
    return 0;
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center pt-[15vh]"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Search input */}
        <div className="flex items-center border-b border-neutral-100 px-4 py-3">
          <Search className="w-5 h-5 text-neutral-400 mr-3 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher partout..."
            className="flex-1 outline-none text-base text-neutral-900 placeholder-neutral-400 bg-transparent"
          />
          {isLoading && (
            <Loader2 className="w-4 h-4 text-neutral-400 animate-spin mr-2 flex-shrink-0" />
          )}
          <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium text-neutral-400 bg-neutral-100 rounded border border-neutral-200">
            Esc
          </kbd>
        </div>

        {/* Results area */}
        <div ref={resultsRef} className="max-h-[60vh] overflow-y-auto">
          {/* Has query + has results */}
          {query.length >= 2 && totalCount > 0 && (
            <div className="py-2">
              {entityOrder.map((entityType) => {
                const items = results.get(entityType) || [];
                const Icon = ENTITY_ICONS[entityType];
                const color = ENTITY_COLORS[entityType];
                const label = ENTITY_LABELS[entityType];

                return (
                  <div key={entityType} className="mb-1">
                    {/* Category header */}
                    <div className="flex items-center px-4 py-1.5 bg-neutral-50">
                      <Icon className={`w-3.5 h-3.5 mr-2 ${color}`} />
                      <span className="text-xs font-semibold uppercase tracking-wider text-neutral-400">
                        {label}
                      </span>
                      <span className="ml-auto text-[10px] font-medium text-neutral-300 bg-neutral-100 px-1.5 py-0.5 rounded-full">
                        {items.length}
                      </span>
                    </div>

                    {/* Results */}
                    {items.map((result, localIdx) => {
                      const fIdx = getFlatIndex(entityType, localIdx);
                      const isActive = fIdx === activeIndex;
                      const ResultIcon = ENTITY_ICONS[result.entityType];

                      return (
                        <button
                          key={result.id}
                          data-index={fIdx}
                          onClick={() => handleResultSelect(result)}
                          onMouseEnter={() => setActiveIndex(fIdx)}
                          className={`w-full flex items-center px-4 py-2.5 text-left transition-colors ${
                            isActive
                              ? 'bg-blue-50 border-l-2 border-[#0066FF]'
                              : 'border-l-2 border-transparent hover:bg-neutral-50'
                          }`}
                        >
                          <ResultIcon className={`w-4 h-4 mr-3 flex-shrink-0 ${color}`} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-neutral-900 truncate">
                              {result.title}
                            </div>
                            {result.subtitle && (
                              <div className="text-xs text-neutral-500 truncate mt-0.5">
                                {result.subtitle}
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          {/* Has query + no results + not loading */}
          {query.length >= 2 && totalCount === 0 && !isLoading && (
            <div className="px-4 py-12 text-center">
              <Search className="w-10 h-10 mx-auto mb-3 text-neutral-200" />
              <p className="text-neutral-500 text-sm">
                Aucun résultat pour <span className="font-medium">« {query} »</span>
              </p>
            </div>
          )}

          {/* No query yet (initial state) */}
          {query.length < 2 && (
            <div className="px-4 py-12 text-center">
              <Search className="w-10 h-10 mx-auto mb-3 text-neutral-200" />
              <p className="text-neutral-400 text-sm">Commencez à taper pour rechercher...</p>
              <p className="text-neutral-300 text-xs mt-2">
                Comptes, contacts, articles, notes, et plus encore
              </p>
            </div>
          )}
        </div>

        {/* Footer with keyboard hints */}
        <div className="flex items-center gap-4 border-t border-neutral-100 px-4 py-2 text-[11px] text-neutral-400">
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-neutral-100 rounded border border-neutral-200 text-[10px]">↑</kbd>
            <kbd className="px-1 py-0.5 bg-neutral-100 rounded border border-neutral-200 text-[10px]">↓</kbd>
            naviguer
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-neutral-100 rounded border border-neutral-200 text-[10px]">↵</kbd>
            sélectionner
          </span>
          <span className="hidden items-center gap-1 sm:flex">
            <kbd className="px-1.5 py-0.5 bg-neutral-100 rounded border border-neutral-200 text-[10px]">Ctrl ↵</kbd>
            en faire une mission
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-neutral-100 rounded border border-neutral-200 text-[10px]">Esc</kbd>
            fermer
          </span>
          {totalCount > 0 && (
            <span className="ml-auto text-neutral-300">
              {totalCount} résultat{totalCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
